import { type GridApi, type GridReadyEvent, type IRowNode } from "ag-grid-community"
import { type AgGridReact } from "ag-grid-react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Button } from "~/components/reusable/Button"
import {
  DialogBodyV2,
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import {
  useAddVariantsToAdjustment,
  useRemoveVariantsFromAdjustment,
} from "~/hooks/useAdjustmentManager"
import { trackEvent } from "~/hooks/useTrackingService"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { AdjustmentResplitWarning } from "~/components/forecast/dialogs/AdjustmentResplitWarning"
import { type AggregatedAdjustment } from "~/models/adjustment"
import { api } from "~/utils/api"
import { formatNumber } from "~/utils/formatting"
import ProductTableV2 from "~/components/planning/ProductTable/ProductTableV2"
import { ProductTableColumn } from "~/utils/productTableUtils"
import { ProductTableType } from "~/constants/productTable"
import { SelectedVariantsProvider } from "~/context/SelectedVariantsProvider"

// Memoized at module level so selection-driven dialog rerenders don't cascade into the table
const MemoizedProductTable = memo(ProductTableV2)

type Props = {
  adjustment: AggregatedAdjustment
  availableVariants: IVariantSummary[]
  open?: boolean
  onClose?: () => void
}

const defaultColumns = [
  ProductTableColumn.Product,
  ProductTableColumn.Forecast,
  ProductTableColumn.Sales,
  ProductTableColumn.Adjustments,
  ProductTableColumn.Tags,
]

export function ManageVariantsInAdjustmentDialog({
  adjustment,
  availableVariants,
  open = false,
  onClose,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(adjustment.variantIds))

  const warehouseId = adjustment.warehouseId === "Global" ? undefined : adjustment.warehouseId

  const { data: adjustmentVariantDetails, isLoading } =
    api.adjustment.getAdjustmentVariantDetails.useQuery(
      { groupId: adjustment.groupId, warehouseId, variantIds: adjustment.variantIds },
      { enabled: open && adjustment.variantIds.length > 0 },
    )

  const addVariants = useAddVariantsToAdjustment()
  const removeVariants = useRemoveVariantsFromAdjustment()

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(adjustment.variantIds))
    }
    didSeedSelectionRef.current = false
  }, [open]) // reset on open; don't add deps that change during open

  // Merge: all variants in the adjustment (with names from API) + available table variants (deduped)
  const allVariants = useMemo((): IVariantSummary[] => {
    const availableSet = new Set(availableVariants.map((v) => v.variantId))

    const fromAdjustment = (adjustmentVariantDetails ?? [])
      .filter((d) => !availableSet.has(d.variantId))
      .map(
        (d) =>
          ({
            variantId: d.variantId,
            variantName: d.variantName,
            productName: d.productName,
            productId: d.productId,
            tags: [],
            adjustmentCount: 0,
            settings: { lifecycleStatus: null, productType: null },
          }) as unknown as IVariantSummary,
      )

    return [...availableVariants, ...fromAdjustment]
  }, [availableVariants, adjustmentVariantDetails])

  const originalIds = useMemo(() => new Set(adjustment.variantIds), [adjustment.variantIds])

  const toAdd = useMemo(
    () => [...selectedIds].filter((id) => !originalIds.has(id)),
    [selectedIds, originalIds],
  )
  const toRemove = useMemo(
    () => [...originalIds].filter((id) => !selectedIds.has(id)),
    [selectedIds, originalIds],
  )

  // Isolated gridRef for the dialog's table — passed to both SelectedVariantsProvider and the
  // table so their selection state is scoped to this dialog and never bleeds into the page table.
  const dialogGridRef = useRef<AgGridReact<IVariantSummary> | null>(null)

  // AG Grid clears row selection whenever row data is replaced, and ProductTableV2 reports every
  // selectionChanged — including the one AG Grid fires on that replacement. The adjustment's
  // variants that aren't already in the page's table selection only arrive once
  // getAdjustmentVariantDetails resolves, so seeding the selection on gridReady alone missed them
  // and the follow-up selectionChanged then overwrote selectedIds with the incomplete set. The
  // dialog opened showing those variants unchecked and pre-staged for removal, so a blind Save
  // would drop them from the adjustment.
  //
  // Fix: seed the grid only once the details query has settled, retrying until rows actually exist,
  // and ignore grid-driven selection until that seed has happened.
  const gridApiRef = useRef<GridApi<IVariantSummary> | null>(null)
  const didSeedSelectionRef = useRef(false)
  // Bumped on gridReady and on every rowDataUpdated, so the seeding effect below re-runs when rows
  // actually land in the grid rather than guessing at AG Grid's internal timing.
  const [gridRowsVersion, setGridRowsVersion] = useState(0)

  const handleGridReady = useCallback((event: GridReadyEvent<IVariantSummary>) => {
    gridApiRef.current = event.api
    const bump = () => setGridRowsVersion((v) => v + 1)
    event.api.addEventListener("rowDataUpdated", bump)
    bump()
  }, [])

  useEffect(() => {
    if (!open || isLoading || didSeedSelectionRef.current) return
    const api = gridApiRef.current
    if (!api) return

    let sawAnyVariantRow = false
    const nodesToSelect: IRowNode<IVariantSummary>[] = []
    api.forEachNode((node) => {
      const variantId = node.data?.variantId
      if (!variantId) return
      sawAnyVariantRow = true
      if (originalIds.has(variantId) && !node.isSelected()) nodesToSelect.push(node)
    })
    // Rows haven't reached the grid yet — a later rowDataUpdated bump re-runs this.
    if (!sawAnyVariantRow) return

    didSeedSelectionRef.current = true
    if (nodesToSelect.length > 0) {
      // setNodesSelected fires a single selectionChanged event instead of one per node
      api.setNodesSelected({ nodes: nodesToSelect, newValue: true })
    }
  }, [open, isLoading, originalIds, gridRowsVersion])

  const handleSelectedVariantsChange = useCallback((ids: string[]) => {
    // Before the seed the grid cannot represent the full membership, so its selection is an
    // artifact of async row loading rather than a user intent.
    if (!didSeedSelectionRef.current) return
    setSelectedIds(new Set(ids))
  }, [])

  const isPending = addVariants.isPending || removeVariants.isPending

  const handleSave = async () => {
    try {
      if (toAdd.length > 0) {
        await addVariants.mutateAsync({
          groupId: adjustment.groupId,
          value: adjustment.value,
          variantIds: toAdd,
          warehouseId,
          channel: adjustment.channels.length === 1 ? adjustment.channels[0] : undefined,
        })
      }
      // Removal runs last on purpose: it re-splits the total over whatever membership is left, so
      // it settles the final shares even when variants were added in the same save.
      if (toRemove.length > 0) {
        const warehouseIds = adjustment.identifiers.map((id) => id.warehouseId)
        await removeVariants.mutateAsync({
          variantIds: toRemove,
          groupId: adjustment.groupId,
          warehouseIds,
          warehouseId,
          channel: adjustment.channels.length === 1 ? adjustment.channels[0] : undefined,
        })
      }
      trackEvent("Adjustment Variants Managed", {
        "Adjustment Name": adjustment.name,
        Added: toAdd.length,
        Removed: toRemove.length,
      })
      onClose?.()
    } catch {
      // errors handled by mutation hooks
    }
  }

  const hasChanges = toAdd.length > 0 || toRemove.length > 0

  return (
    <DialogRoot
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose?.()
      }}
    >
      <DialogContentV2 className="max-w-[75vw] max-h-[70vh]">
        <DialogHeaderV2>
          <DialogTitle>Manage variants in &quot;{adjustment.name}&quot;</DialogTitle>
        </DialogHeaderV2>

        <DialogBodyV2 className="flex flex-col gap-3 overflow-y-auto">
          <p className="text-xs text-fg-subtle">
            Select which variants are in this adjustment. Changes are applied on save.
          </p>

          <SelectedVariantsProvider gridRef={dialogGridRef}>
            <MemoizedProductTable
              id="manage-variants-adjustment"
              variants={allVariants}
              type={ProductTableType.None}
              loading={isLoading}
              height="500px"
              minHeight={0}
              initialAggregation={"Variant"}
              enableFilter
              enableSearch
              defaultColumnIdsToDisplay={defaultColumns}
              gridRef={dialogGridRef}
              onGridReady={handleGridReady}
              onSelectedVariantsChange={handleSelectedVariantsChange}
            />
          </SelectedVariantsProvider>
        </DialogBodyV2>

        {(toAdd.length > 0 || toRemove.length > 0) && (
          <div className="px-5 pb-4">
            <AdjustmentResplitWarning
              adjustment={adjustment}
              addingCount={toAdd.length}
              removingCount={toRemove.length}
            />
          </div>
        )}

        <DialogFooterV2 className="flex justify-between">
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
          <div className="flex flex-row items-center gap-4">
            {hasChanges && (
              <p className="text-xs text-fg-subtle">
                {toAdd.length > 0 && (
                  <span className="text-green-600">{formatNumber(toAdd.length, 0)} added </span>
                )}
                {toRemove.length > 0 && (
                  <span className="text-red-500">{formatNumber(toRemove.length, 0)} removed</span>
                )}
              </p>
            )}
            <Button requires="edit" onClick={handleSave} disabled={!hasChanges} loading={isPending}>
              Save changes
            </Button>
          </div>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
